import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import { UserReviewsResponse, UserMatchWithReviews } from '../types';
import { useNotification } from './Notification';
import { UnifiedHelpButton, SpecificHelpModal } from './Help';
import { useResponsive } from '../hooks/useResponsive';

const ReviewsPage: React.FC = () => {
  const [userReviews, setUserReviews] = useState<UserReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [expandedMatches, setExpandedMatches] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [matchesPerPage, setMatchesPerPage] = useState(5);
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { isMobile } = useResponsive();

  useEffect(() => {
    fetchUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('Debes iniciar sesión para ver tus reviews', 'error');
        navigate('/auth');
        return;
      }

      // Obtener información del usuario actual
      const userResponse = await fetch(getApiUrl('/api/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!userResponse.ok) {
        showNotification('Error obteniendo información del usuario', 'error');
        return;
      }

      const userData = await userResponse.json();
      setUser(userData);
      fetchUserReviews(userData.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user data:', error);
      showNotification('Error cargando datos del usuario', 'error');
    }
  };

  const fetchUserReviews = async (userId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/reviews/user/${userId}/received`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserReviews(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando reviews', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user reviews:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatReviewDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes}`;
  };

  const formatMatchDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toggleMatchExpansion = (matchId: string) => {
    const newExpanded = new Set(expandedMatches);
    if (newExpanded.has(matchId)) {
      newExpanded.delete(matchId);
    } else {
      newExpanded.add(matchId);
    }
    setExpandedMatches(newExpanded);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedMatches(new Set()); // Reset expanded matches when changing page
  };

  const handleMatchesPerPageChange = (newValue: number) => {
    setMatchesPerPage(newValue);
    setCurrentPage(1); // Reset to first page
    setExpandedMatches(new Set()); // Reset expanded matches
  };

  // Calcular partidos para la página actual
  const getCurrentPageMatches = () => {
    if (!userReviews) return [];
    
    const startIndex = (currentPage - 1) * matchesPerPage;
    const endIndex = startIndex + matchesPerPage;
    return userReviews.matchesWithReviews.slice(startIndex, endIndex);
  };

  // Calcular total de páginas
  const totalPages = userReviews ? Math.ceil(userReviews.matchesWithReviews.length / matchesPerPage) : 0;

  // Generar array de páginas para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#181A1B',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', animation: 'spin 1s linear infinite' }}>⚽</div>
          <p>Cargando reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#181A1B',
      padding: isMobile ? '12px' : '20px',
      paddingTop: isMobile ? '80px' : '20px' // Espacio para el header fijo
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '16px',
        marginBottom: isMobile ? '16px' : '24px',
        padding: isMobile ? '12px' : '16px',
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '8px' : '12px',
        border: '1px solid #33383B'
      }}>
        <button
          onClick={() => navigate('/profile')}
          style={{
            padding: isMobile ? '6px 8px' : '8px 12px',
            backgroundColor: '#40444B',
            border: 'none',
            borderRadius: isMobile ? '6px' : '8px',
            color: '#FFFFFF',
            cursor: 'pointer',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '600'
          }}
        >
          {isMobile ? '←' : '← Volver al Perfil'}
        </button>
        <h1 style={{
          margin: 0,
          fontSize: isMobile ? '18px' : '24px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          textAlign: 'center',
          flex: 1
        }}>
          {isMobile ? '⭐ Reviews' : '⭐ Mis Reviews Recibidas'}
        </h1>
      </div>

      {/* Controles de paginación */}
      {userReviews && userReviews.matchesWithReviews.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '12px' : '0',
          marginBottom: isMobile ? '16px' : '20px',
          padding: isMobile ? '12px' : '16px',
          backgroundColor: '#23272A',
          borderRadius: isMobile ? '8px' : '12px',
          border: '1px solid #33383B'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '12px',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <span style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '12px' : '14px',
              whiteSpace: 'nowrap'
            }}>
              {isMobile ? 'Por página:' : 'Partidos por página:'}
            </span>
            <select
              value={matchesPerPage}
              onChange={(e) => handleMatchesPerPageChange(Number(e.target.value))}
              style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                backgroundColor: '#40444B',
                border: '1px solid #555',
                borderRadius: isMobile ? '4px' : '6px',
                color: '#FFFFFF',
                fontSize: isMobile ? '12px' : '14px',
                cursor: 'pointer'
              }}
            >
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '4px' : '8px',
            justifyContent: isMobile ? 'center' : 'flex-end',
            flexWrap: 'wrap'
          }}>
            <span style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '12px' : '14px'
            }}>
              Página {currentPage} de {totalPages}
            </span>
            <span style={{ 
              color: '#B0BEC5', 
              fontSize: isMobile ? '12px' : '14px'
            }}>
              ({userReviews.matchesWithReviews.length} partidos total)
            </span>
          </div>
        </div>
      )}

      {/* Contenido */}
      {userReviews && userReviews.matchesWithReviews.length > 0 ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? '16px' : '24px' 
        }}>
          {getCurrentPageMatches().map((matchWithReviews) => {
            const isExpanded = expandedMatches.has(matchWithReviews.match.id);
            
            return (
              <div key={matchWithReviews.match.id} style={{
                backgroundColor: '#23272A',
                borderRadius: isMobile ? '12px' : '16px',
                border: '1px solid #33383B',
                overflow: 'hidden'
              }}>
                {/* Header del partido - Siempre visible */}
                <button
                  type="button"
                  onClick={() => toggleMatchExpansion(matchWithReviews.match.id)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: isMobile ? '16px' : '20px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: isExpanded ? '#2C2F33' : '#23272A',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isExpanded ? '#40444B' : '#2C2F33';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isExpanded ? '#2C2F33' : '#23272A';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h2 style={{
                      margin: '0 0 6px 0',
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 'bold',
                      color: '#FFFFFF',
                      lineHeight: '1.3'
                    }}>
                      📍 {matchWithReviews.match.location}
                    </h2>
                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#B0BEC5',
                      lineHeight: '1.4'
                    }}>
                      {formatMatchDate(matchWithReviews.match.date)} • {matchWithReviews.match.time}
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '6px' : '8px', 
                      flexWrap: 'wrap' 
                    }}>
                      <span style={{
                        backgroundColor: '#40444B',
                        color: '#B0BEC5',
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        borderRadius: isMobile ? '6px' : '8px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 'bold'
                      }}>
                        {matchWithReviews.reviews.length} review{matchWithReviews.reviews.length !== 1 ? 's' : ''}
                      </span>
                      <span style={{
                        backgroundColor: matchWithReviews.match.status === 'FINISHED' ? '#00E676' : '#FFD600',
                        color: matchWithReviews.match.status === 'FINISHED' ? '#181A1B' : '#181A1B',
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        borderRadius: isMobile ? '6px' : '8px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 'bold'
                      }}>
                        {matchWithReviews.match.status === 'FINISHED' ? 'Finalizado' : 'En Progreso'}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px'
                  }}>
                    <div style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      fontSize: isMobile ? '16px' : '20px',
                      color: '#B0BEC5'
                    }}>
                      ▼
                    </div>
                  </div>
                </button>

                {/* Reviews del partido - Solo visible si está expandido */}
                {isExpanded && (
                  <div style={{
                    padding: isMobile ? '0 16px 16px 16px' : '0 20px 20px 20px',
                    borderTop: '1px solid #40444B'
                  }}>
                    {!matchWithReviews.canViewReviews ? (
                      <div style={{
                        textAlign: 'center',
                        padding: isMobile ? '24px 16px' : '28px 20px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '12px',
                        border: '1px solid #40444B',
                        marginTop: isMobile ? '12px' : '16px'
                      }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>👀</div>
                        <p style={{ margin: '0 0 6px 0', fontSize: isMobile ? '14px' : '15px', color: '#FFFFFF', fontWeight: 'bold' }}>
                          Te calificaron {Math.max(1, matchWithReviews.reviewsReceived)} {Math.max(1, matchWithReviews.reviewsReceived) === 1 ? 'vez' : 'veces'} en este partido
                        </p>
                        <p style={{ margin: '0 0 16px 0', fontSize: isMobile ? '12px' : '13px', color: '#B0BEC5', lineHeight: '1.4' }}>
                          Dejá tu reseña y descubrí qué te pusieron tus compañeros
                        </p>
                        <button
                          onClick={() => navigate(`/match/${matchWithReviews.match.id}/rate`)}
                          style={{
                            backgroundColor: '#00C853',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '8px',
                            padding: isMobile ? '10px 18px' : '12px 22px',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          Calificar ahora →
                        </button>
                      </div>
                    ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '10px' : '12px',
                      marginTop: isMobile ? '12px' : '16px'
                    }}>
                      {matchWithReviews.reviews.map((review) => (
                        <div key={review.id} style={{
                          backgroundColor: '#2C2F33',
                          borderRadius: isMobile ? '8px' : '12px',
                          padding: isMobile ? '12px' : '16px',
                          border: '1px solid #40444B'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: isMobile ? '8px' : '12px'
                          }}>
                            <div>
                              <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: '#B0BEC5'
                              }}>
                                {/* Solo mostrar la fecha, no el reviewer */}
                                {formatReviewDate(review.createdAt)}
                              </div>
                            </div>
                          </div>
                          
                          {review.comment && (
                            <div style={{
                              fontSize: isMobile ? '13px' : '14px',
                              color: '#FFFFFF',
                              marginBottom: isMobile ? '10px' : '12px',
                              fontStyle: 'italic',
                              backgroundColor: '#40444B',
                              padding: isMobile ? '10px' : '12px',
                              borderRadius: isMobile ? '6px' : '8px',
                              borderLeft: '4px solid #00E676',
                              lineHeight: '1.4'
                            }}>
                              "{review.comment}"
                            </div>
                          )}

                          <div style={{ 
                            display: 'flex', 
                            gap: isMobile ? '6px' : '8px', 
                            flexWrap: 'wrap' 
                          }}>
                            {/* Tags predefinidos positivos */}
                            {review.positiveTags.map((tag: string) => (
                              <span key={tag} style={{
                                backgroundColor: '#00E676',
                                color: '#181A1B',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: isMobile ? '8px' : '12px',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: 'bold'
                              }}>
                                {tag}
                              </span>
                            ))}
                            {/* Tags predefinidos negativos */}
                            {review.negativeTags.map((tag: string) => (
                              <span key={tag} style={{
                                backgroundColor: '#FF6B6B',
                                color: '#FFFFFF',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: isMobile ? '8px' : '12px',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: 'bold'
                              }}>
                                {tag}
                              </span>
                            ))}
                            {/* Tags personalizados positivos */}
                            {review.customPositiveTags && review.customPositiveTags.map((tag: string) => (
                              <span key={tag} style={{
                                backgroundColor: '#00BFA5',
                                color: '#FFFFFF',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: isMobile ? '8px' : '12px',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: 'bold',
                                border: '1px solid #FFFFFF'
                              }}>
                                {tag}
                              </span>
                            ))}
                            {/* Tags personalizados negativos */}
                            {review.customNegativeTags && review.customNegativeTags.map((tag: string) => (
                              <span key={tag} style={{
                                backgroundColor: '#D32F2F',
                                color: '#FFFFFF',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: isMobile ? '8px' : '12px',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: 'bold',
                                border: '1px solid #FFFFFF'
                              }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#B0BEC5'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>⭐</div>
          <h2 style={{
            fontSize: '24px',
            margin: '0 0 12px 0',
            color: '#FFFFFF'
          }}>
            No tienes reviews aún
          </h2>
          <p style={{
            fontSize: '16px',
            margin: '0 0 24px 0'
          }}>
            Las reviews aparecerán aquí después de que tus compañeros te evalúen en los partidos
          </p>
          <button
            onClick={() => navigate('/profile')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#00E676',
              border: 'none',
              borderRadius: '8px',
              color: '#181A1B',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Volver al Perfil
          </button>
        </div>
      )}

      {/* Paginación */}
      {userReviews && userReviews.matchesWithReviews.length > 0 && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: isMobile ? '4px' : '8px',
          marginTop: isMobile ? '24px' : '32px',
          padding: isMobile ? '16px' : '20px',
          backgroundColor: '#23272A',
          borderRadius: isMobile ? '8px' : '12px',
          border: '1px solid #33383B',
          flexWrap: 'wrap'
        }}>
          {/* Botón Anterior */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: isMobile ? '6px 10px' : '8px 12px',
              backgroundColor: currentPage === 1 ? '#40444B' : '#00E676',
              border: 'none',
              borderRadius: isMobile ? '4px' : '6px',
              color: currentPage === 1 ? '#666' : '#181A1B',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600'
            }}
          >
            {isMobile ? '←' : '← Anterior'}
          </button>

          {/* Números de página */}
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
              disabled={page === '...'}
              style={{
                padding: isMobile ? '6px 10px' : '8px 12px',
                backgroundColor: page === currentPage ? '#00E676' : '#40444B',
                border: 'none',
                borderRadius: isMobile ? '4px' : '6px',
                color: page === currentPage ? '#181A1B' : '#FFFFFF',
                cursor: page === '...' ? 'default' : 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                minWidth: isMobile ? '32px' : '40px'
              }}
            >
              {page}
            </button>
          ))}

          {/* Botón Siguiente */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: isMobile ? '6px 10px' : '8px 12px',
              backgroundColor: currentPage === totalPages ? '#40444B' : '#00E676',
              border: 'none',
              borderRadius: isMobile ? '4px' : '6px',
              color: currentPage === totalPages ? '#666' : '#181A1B',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600'
            }}
          >
            {isMobile ? '→' : 'Siguiente →'}
          </button>
        </div>
      )}

      {/* Modal de Ayuda Contextual */}
      <SpecificHelpModal 
        isOpen={showHelp} 
        onClose={() => setShowHelp(false)} 
        context="reviews"
      />


      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReviewsPage; 